/**
 * Mobile · Log Trip (Phase 3)
 *
 * Mobile-friendly trip log capture for support workers.
 *
 *  • Loads the staff's registered personal vehicles + the org transport settings
 *  • Captures distance via direct km OR start/end odometer
 *  • Optional purpose, notes, shift_id (passed via URL ?shift_id=)
 *  • Submits POST /api/transport/logs
 *  • Offline-capable: when offline (or POST fails with a network error), the
 *    payload is queued via the existing offline action queue with
 *    actionType 'TRANSPORT_LOG_CREATE'. The user gets feedback and can
 *    continue working; the standard sync engine flushes when connectivity
 *    returns.
 *
 *  Strict Phase-1 invariant: the backend approve/reject is what eventually
 *  triggers preview math. No finance side effects are emitted from this page.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Car, Save, Loader2, AlertTriangle, Wifi, WifiOff, ChevronLeft, Camera, ClipboardList } from 'lucide-react';
import { toast } from 'sonner';
import { isOnline } from '@/lib/offline/syncEngine';
import { queueOfflineAction, ACTION_TYPES } from '@/lib/offline/actionQueue';

// BS3-026: `portal` renders the same form inside the desktop Staff Portal (/staff/transport/new) — the portal shell supplies the
// title and back navigation, the sections sit side by side on wide screens and the submit button is not full width.
export default function MobileTransportLogPage({ portal = false }) {
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const shiftId = search.get('shift_id') || '';

  const [personalVehicles, setPersonalVehicles] = useState([]);
  const [fleetVehicles, setFleetVehicles] = useState([]);
  const [fleetLoaded, setFleetLoaded] = useState(false);
  const [settings, setSettings] = useState(null);
  const [featureDisabled, setFeatureDisabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [online, setOnlineState] = useState(isOnline());
  const [pending, setPending] = useState([]);

  const [form, setForm] = useState({
    vehicle_kind: 'personal',
    vehicle_id: '',
    purpose: 'during_activity',
    distance_km: '',
    start_odometer: '',
    end_odometer: '',
    start_location: '',
    end_location: '',
    notes: '',
    photo_url: '',
    start_odometer_photo_url: '',
    end_odometer_photo_url: '',
    manual_distance_reason: '',
  });

  useEffect(() => {
    const updateNet = () => setOnlineState(isOnline());
    window.addEventListener('online', updateNet);
    window.addEventListener('offline', updateNet);
    return () => {
      window.removeEventListener('online', updateNet);
      window.removeEventListener('offline', updateNet);
    };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [vRes, sRes] = await Promise.all([
        api.get('/transport/staff-vehicles').catch(e => e.response),
        api.get('/transport/settings').catch(e => e.response),
      ]);
      const featureOff = (vRes?.status === 403 && vRes?.data?.error?.code === 'FEATURE_DISABLED') ||
                         (sRes?.status === 403 && sRes?.data?.error?.code === 'FEATURE_DISABLED');
      if (featureOff) {
        setFeatureDisabled(true);
      } else {
        const list = (vRes?.data?.data || []).filter(v => v.active !== false);
        setPersonalVehicles(list);
        setSettings(sRes?.data?.data || null);
        if (list.length === 1) setForm(f => ({ ...f, vehicle_id: list[0].id || list[0]._id }));
      }
    } finally { setLoading(false); }
  }, []);

  // Lazy-load fleet vehicles the first time the user switches to "company".
  const loadFleet = useCallback(async () => {
    if (fleetLoaded) return;
    try {
      const r = await api.get('/transport/fleet-vehicles');
      const list = (r?.data?.data || []).filter(v => v.active !== false && !v.is_deleted);
      setFleetVehicles(list);
      setFleetLoaded(true);
    } catch (err) {
      const code = err?.response?.data?.error?.code;
      if (code === 'FEATURE_DISABLED') {
        setFeatureDisabled(true);
      } else if (err?.response?.status === 403) {
        toast.error('You do not have access to the fleet vehicle list');
      } else {
        toast.error('Could not load fleet vehicles');
      }
      setFleetVehicles([]);
      setFleetLoaded(true); // avoid retry storm
    }
  }, [fleetLoaded]);

  // Active dropdown source depends on the selected vehicle_kind.
  const activeVehicleList =
    form.vehicle_kind === 'company' ? fleetVehicles : personalVehicles;

  useEffect(() => { load(); }, [load]);

  // Day 40 — staff status view: own trips awaiting approval.
  const loadPending = useCallback(() => {
    api.get('/transport/my-pending')
      .then(r => setPending(r.data?.data || []))
      .catch(() => {});
  }, []);
  useEffect(() => { loadPending(); }, [loadPending]);

  // Day 39 — capture an odometer photo as a compact data URL.
  const capturePhoto = (key) => (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!window.confirm('Privacy notice: only capture the odometer. Avoid faces or personal information. Continue?')) {
      e.target.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setForm(f => ({ ...f, [key]: reader.result }));
    reader.readAsDataURL(file);
  };

  // Day 40 — manual distance change detection (km differs from odometer pair).
  const odoKm = (form.start_odometer !== '' && form.end_odometer !== '')
    ? Math.round((Number(form.end_odometer) - Number(form.start_odometer)) * 100) / 100 : null;
  const manualDistanceChanged = form.distance_km !== '' && odoKm != null &&
    Math.abs(Number(form.distance_km) - odoKm) > 0.01;

  const previewKm = (() => {
    if (form.distance_km !== '') return Number(form.distance_km);
    if (form.start_odometer !== '' && form.end_odometer !== '') {
      const d = Number(form.end_odometer) - Number(form.start_odometer);
      return d >= 0 ? d : NaN;
    }
    return null;
  })();

  // Participant-billable readiness — need a shift AND a configured NDIS rate.
  // Falls back to default 0.97 only if settings hasn't resolved yet, so we
  // require an explicit `settings` object before treating the rate as known.
  const ndisRate = settings?.ndis_rate_per_km;
  const ndisRateKnown = settings != null && ndisRate != null && Number(ndisRate) > 0;
  const participantBillableReady = !!shiftId && ndisRateKnown && previewKm != null && !isNaN(previewKm) && previewKm > 0;
  const previewNdis = participantBillableReady
    ? Math.round(previewKm * Number(ndisRate) * 100) / 100
    : 0;
  const previewReimb = previewKm != null && !isNaN(previewKm)
    ? Math.round(previewKm * (settings?.staff_reimbursement_rate_per_km ?? 0.88) * 100) / 100 : 0;

  const submit = async () => {
    if (!form.vehicle_id) { toast.error('Pick a vehicle'); return; }
    // Cross-kind submission guard: the chosen vehicle_id MUST exist in the
    // list that matches the chosen vehicle_kind. Backend re-validates, but
    // this client-side check stops the call before the network hop.
    const inActiveList = activeVehicleList.some(
      v => (v.id || v._id) === form.vehicle_id
    );
    if (!inActiveList) {
      toast.error(
        form.vehicle_kind === 'company'
          ? 'Selected vehicle is not a fleet vehicle. Pick from the company list.'
          : 'Selected vehicle is not your personal vehicle. Pick from your list.'
      );
      return;
    }
    if (previewKm == null || isNaN(previewKm) || previewKm <= 0) { toast.error('Enter distance or start+end odometer'); return; }
    // Day 40 — manual distance change requires a reason.
    if (manualDistanceChanged && !form.manual_distance_reason.trim()) {
      toast.error('A reason is required when the distance differs from the odometer reading');
      return;
    }
    const payload = {
      offlineId: (window.crypto?.randomUUID?.() || `trip-${Date.now()}-${Math.random().toString(36).slice(2)}`),
      shift_id: shiftId || null,
      vehicle_kind: form.vehicle_kind,
      vehicle_id: form.vehicle_id,
      purpose: form.purpose,
      distance_km: previewKm,
      start_odometer: form.start_odometer === '' ? null : Number(form.start_odometer),
      end_odometer: form.end_odometer === '' ? null : Number(form.end_odometer),
      start_location: form.start_location || null,
      end_location: form.end_location || null,
      notes: form.notes,
      photo_url: form.photo_url || null,
      start_odometer_photo_url: form.start_odometer_photo_url || null,
      end_odometer_photo_url: form.end_odometer_photo_url || null,
      manual_distance_override: manualDistanceChanged,
      manual_distance_reason: form.manual_distance_reason || null,
    };

    setSubmitting(true);
    try {
      if (!online) {
        await queueOfflineAction(ACTION_TYPES.TRANSPORT_LOG_CREATE, payload);
        toast.success('Queued offline', { description: 'Trip will sync when you reconnect.' });
        navigate(-1);
        return;
      }
      const res = await api.post('/transport/logs', payload);
      const trip = res.data?.data || {};
      if (trip.has_exceptions) {
        toast.warning('Submitted for review', {
          description: `Flagged: ${(trip.exceptions || []).map(x => x.code).join(', ')}`,
        });
      } else if (trip.status === 'AUTO_APPROVED') {
        toast.success('Trip logged & auto-approved');
      } else {
        toast.success('Trip submitted for approval');
      }
      loadPending();
      navigate(-1);
    } catch (err) {
      // Network failure path → queue offline.
      const isNetwork = !err?.response;
      if (isNetwork) {
        try {
          await queueOfflineAction(ACTION_TYPES.TRANSPORT_LOG_CREATE, payload);
          toast.success('Saved offline', { description: 'Trip will sync when you reconnect.' });
          navigate(-1);
          return;
        } catch (qErr) {
          toast.error('Offline queue failed', { description: qErr?.message });
        }
      } else {
        toast.error('Failed to log trip', { description: err?.response?.data?.error?.message || err?.message });
      }
    } finally { setSubmitting(false); }
  };

  if (featureDisabled) {
    return (
      <div className="p-4 space-y-4" data-testid="mobile-transport-disabled">
        <h1 className="text-xl font-semibold">Log a trip</h1>
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            The transport module is currently disabled. Contact an administrator to enable it.
          </AlertDescription>
        </Alert>
        <Button variant="outline" onClick={() => navigate(-1)}><ChevronLeft className="h-4 w-4 mr-1" />Back</Button>
      </div>
    );
  }

  if (loading) {
    return <div className="flex items-center justify-center py-12 text-muted-foreground"><Loader2 className="h-4 w-4 mr-2 animate-spin" />Loading…</div>;
  }

  return (
    <div className={portal ? 'space-y-4' : 'p-4 space-y-4 max-w-md mx-auto'} data-testid={portal ? 'staff-transport-log-page' : 'mobile-transport-log-page'}>
      <div className={portal ? 'flex items-center justify-end' : 'flex items-center justify-between'}>
        {!portal && (
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="mobile-back-btn"><ChevronLeft className="h-4 w-4" /></Button>
          <h1 className="text-lg font-semibold">Log a trip</h1>
        </div>
        )}
        <Badge variant={online ? 'default' : 'destructive'} className="text-[10px]">
          {online ? <><Wifi className="h-3 w-3 mr-1" />Online</> : <><WifiOff className="h-3 w-3 mr-1" />Offline</>}
        </Badge>
      </div>

      {pending.length > 0 && (
        <Card className="border-amber-500/30 bg-amber-500/5" data-testid="transport-pending-list">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <ClipboardList className="h-4 w-4 text-amber-600" />
              Pending approval ({pending.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {pending.map(t => (
              <div key={t.id || t._id} className="rounded-md border bg-background p-2 text-xs" data-testid={`pending-trip-${t.id || t._id}`}>
                <div className="flex justify-between">
                  <span className="font-medium">{t.distance_km} km · {t.purpose?.replace(/_/g, ' ')}</span>
                  <Badge variant="outline" className="text-[10px]">{t.status}</Badge>
                </div>
                {(t.exceptions || []).length > 0 && (
                  <p className="text-amber-700 mt-0.5">⚠ {(t.exceptions || []).map(x => x.code).join(', ')}</p>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {!shiftId && (
        <Alert>
          <AlertDescription className="text-xs">
            No shift attached — this trip will be saved as a personal log only. Open from your shift to capture NDIS-billable trips.
          </AlertDescription>
        </Alert>
      )}

      <div className={portal ? 'grid gap-4 lg:grid-cols-2 items-start' : 'space-y-4'} data-testid="transport-form-sections">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2"><Car className="h-4 w-4" />Vehicle</CardTitle>
          <CardDescription className="text-xs">{shiftId ? `Shift: ${shiftId.slice(0, 12)}…` : 'No shift selected'}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label>Vehicle kind</Label>
            <select className="w-full border rounded-md h-11 px-2 bg-background text-base"
                    value={form.vehicle_kind}
                    onChange={e => {
                      const newKind = e.target.value;
                      // Reset vehicle_id when kind changes — current selection
                      // belongs to the wrong list.
                      setForm({ ...form, vehicle_kind: newKind, vehicle_id: '' });
                      if (newKind === 'company') loadFleet();
                    }}
                    data-testid="mobile-vehicle-kind">
              <option value="personal">Personal</option>
              <option value="company">Company / Fleet</option>
            </select>
            {form.vehicle_kind === 'company' && (
              <p
                className="text-xs text-muted-foreground mt-1"
                data-testid="mobile-reimb-hint-company"
              >
                Company vehicle — no staff reimbursement.
              </p>
            )}
          </div>
          <div>
            <Label>Vehicle *</Label>
            <select className="w-full border rounded-md h-11 px-2 bg-background text-base"
                    value={form.vehicle_id} onChange={e => setForm({ ...form, vehicle_id: e.target.value })}
                    data-testid="mobile-vehicle-id">
              <option value="">Select vehicle…</option>
              {activeVehicleList.map(v => (
                <option key={v.id || v._id} value={v.id || v._id}>
                  {v.label || v.make_model || v.registration} ({v.registration || v.rego || 'no rego'})
                </option>
              ))}
            </select>
            {activeVehicleList.length === 0 && (
              <p className="text-xs text-muted-foreground mt-1" data-testid="mobile-vehicle-empty-hint">
                {form.vehicle_kind === 'company'
                  ? 'No fleet vehicles available for your branch.'
                  : 'No personal vehicles registered. Ask an admin to register yours.'}
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Distance</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label>Distance (km)</Label>
            <Input type="number" inputMode="decimal" step="0.1" value={form.distance_km}
                   onChange={e => setForm({ ...form, distance_km: e.target.value })}
                   className="h-11 text-base"
                   placeholder="e.g. 12.5"
                   data-testid="mobile-distance-km" />
          </div>
          <p className="text-xs text-center text-muted-foreground">— or —</p>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Start odometer</Label>
              <Input type="number" inputMode="decimal" value={form.start_odometer}
                     onChange={e => setForm({ ...form, start_odometer: e.target.value })}
                     className="h-11 text-base" data-testid="mobile-odo-start" />
            </div>
            <div>
              <Label>End odometer</Label>
              <Input type="number" inputMode="decimal" value={form.end_odometer}
                     onChange={e => setForm({ ...form, end_odometer: e.target.value })}
                     className="h-11 text-base" data-testid="mobile-odo-end" />
            </div>
          </div>
          {previewKm != null && !isNaN(previewKm) && previewKm > 0 && (
            <div className="rounded-md bg-muted p-3 text-sm space-y-1" data-testid="mobile-preview">
              <div className="flex justify-between"><span>Distance</span><strong>{previewKm} km</strong></div>
              {participantBillableReady ? (
                <div
                  className="flex justify-between"
                  data-testid="mobile-preview-participant-billable"
                >
                  <span>Participant billable</span>
                  <strong>${previewNdis.toFixed(2)}</strong>
                </div>
              ) : (
                <div
                  className="flex justify-between text-muted-foreground"
                  data-testid="mobile-preview-participant-billable-pending"
                >
                  <span>Participant billable</span>
                  <strong className="text-xs italic">pending shift details</strong>
                </div>
              )}
              {form.vehicle_kind === 'company' ? (
                <div
                  className="flex justify-between text-muted-foreground"
                  data-testid="mobile-preview-reimb-company"
                >
                  <span>Reimbursement</span>
                  <strong>Company vehicle — no staff reimbursement</strong>
                </div>
              ) : (
                <div
                  className="flex justify-between"
                  data-testid="mobile-preview-reimb-personal"
                >
                  <span>Estimated reimbursement</span>
                  <strong>${previewReimb.toFixed(2)}</strong>
                </div>
              )}
              <div className="text-[10px] text-muted-foreground pt-1 border-t">Preview only — not deducted until approval.</div>
            </div>
          )}
          {manualDistanceChanged && (
            <div data-testid="manual-distance-reason-wrap">
              <Label>Reason for manual distance change *</Label>
              <Input value={form.manual_distance_reason}
                     onChange={e => setForm({ ...form, manual_distance_reason: e.target.value })}
                     placeholder="e.g. detour due to roadworks"
                     className="h-11 text-base" data-testid="manual-distance-reason" />
              <p className="text-[11px] text-amber-700 mt-1">
                Entered km ({form.distance_km}) differs from odometer ({odoKm} km) — this trip will be sent for review.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Camera className="h-4 w-4" />Odometer photos &amp; route</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <p className="text-[11px] text-muted-foreground">
            Capture the start &amp; end odometer for claim accuracy. Privacy: photograph the dashboard only.
          </p>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Start odometer photo</Label>
              <input type="file" accept="image/*" capture="environment" onChange={capturePhoto('start_odometer_photo_url')}
                     className="block w-full text-xs" data-testid="odo-start-photo" />
              {form.start_odometer_photo_url && <img src={form.start_odometer_photo_url} alt="start" className="mt-1 h-16 w-full object-cover rounded" data-testid="odo-start-preview" />}
            </div>
            <div>
              <Label>End odometer photo</Label>
              <input type="file" accept="image/*" capture="environment" onChange={capturePhoto('end_odometer_photo_url')}
                     className="block w-full text-xs" data-testid="odo-end-photo" />
              {form.end_odometer_photo_url && <img src={form.end_odometer_photo_url} alt="end" className="mt-1 h-16 w-full object-cover rounded" data-testid="odo-end-preview" />}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Start location</Label>
              <Input value={form.start_location} onChange={e => setForm({ ...form, start_location: e.target.value })}
                     className="h-11 text-base" placeholder="e.g. home" data-testid="trip-start-location" />
            </div>
            <div>
              <Label>End location</Label>
              <Input value={form.end_location} onChange={e => setForm({ ...form, end_location: e.target.value })}
                     className="h-11 text-base" placeholder="e.g. day program" data-testid="trip-end-location" />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Notes</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label>Purpose</Label>
            <select className="w-full border rounded-md h-11 px-2 bg-background text-base"
                    value={form.purpose} onChange={e => setForm({ ...form, purpose: e.target.value })}
                    data-testid="mobile-purpose">
              <option value="during_activity">During activity</option>
              <option value="to_activity">To activity</option>
              <option value="from_activity">From activity</option>
              <option value="admin">Admin / non-billable</option>
            </select>
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                      placeholder="(optional)"
                      data-testid="mobile-notes" />
          </div>
          <div>
            <Label>Odometer photo URL (optional)</Label>
            <Input value={form.photo_url} onChange={e => setForm({ ...form, photo_url: e.target.value })}
                   placeholder="paste uploaded photo URL"
                   className="h-11 text-base"
                   data-testid="mobile-photo-url" />
          </div>
        </CardContent>
      </Card>
      </div>

      <div className={portal ? 'flex justify-end gap-2' : ''}>
        {portal && <Button variant="outline" onClick={() => navigate(-1)} disabled={submitting} data-testid="staff-cancel-trip">Cancel</Button>}
        <Button className={portal ? '' : 'w-full h-12 text-base'} onClick={submit} disabled={submitting} data-testid="mobile-submit-trip">
          {submitting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
          {online ? 'Log trip' : 'Save offline'}
        </Button>
      </div>
    </div>
  );
}
